import React from 'react';
import { FiCopy, FiSearch, FiChevronDown, FiThumbsUp, FiMessageCircle, FiDownload } from 'react-icons/fi';
import { toast } from '../../Toast';
import { useClient } from '../clientSettingsData';

// the client's site as the widget pages print it: "49metals.com.au"
export const useWidgetSite = () => {
    const { stock, short, ticker, name } = useClient();
    const host = (stock?.websiteUrl || 'https://49metals.com.au').replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '');
    return { host, short, ticker, name };
};

export const copyText = (text: string, what = 'code') => {
    if (!navigator.clipboard) {
        toast.error('Could not copy — select it and copy it by hand');
        return;
    }
    navigator.clipboard.writeText(text).then(
        () => toast.success(`Copied the ${what}`),
        () => toast.error('Could not copy — select it and copy it by hand'));
};

export const CopyButton: React.FC<{ text: string; what?: string; className?: string }> = ({ text, what, className }) =>
    <button type="button" className={`wd-copy${className ? ` ${className}` : ''}`} onClick={() => copyText(text, what)}><FiCopy /> Copy</button>;

// grey (shortcode) or navy (HTML) code box with a Copy button under the code
export const CodeBox: React.FC<{ code: string; dark?: boolean }> = ({ code, dark }) =>
    <div className={`wd-code${dark ? ' dark' : ''}`}>
        <pre>{code}</pre>
        <CopyButton text={code} />
    </div>;

// ---- pieces the document-list demos share (announcements, presentations, reports, meetings) ----

export const DemoHead: React.FC<{ title: string; sub: string }> = ({ title, sub }) =>
    <div className="wd-demo-head"><h3>{title}</h3><p>{sub}</p></div>;

export const DemoSearch: React.FC<{ placeholder: string; filters?: string[]; showSearch?: boolean }> = ({ placeholder, filters = [], showSearch = true }) =>
    showSearch || filters.length > 0
        ? <div className="wd-demo-search">
            {showSearch && <span className="search"><FiSearch /> {placeholder}</span>}
            {filters.map(f => <span key={f} className="filter">{f} <FiChevronDown /></span>)}
        </div>
        : null;

export interface DocItem {
    title: string;
    meta: string;
    badge?: string;
    likes?: number;
    questions?: number;
}

interface DocListProps {
    items: DocItem[];
    showBadge?: boolean;
    likes?: boolean;
    likeCount?: boolean;
    questionCount?: boolean;
    download?: boolean;
}

// rows: title + meta, a grey badge, like and question counts, download icon. The first like count is highlighted as in the spec.
export const DocList: React.FC<DocListProps> = ({ items, showBadge = true, likes = true, likeCount = true, questionCount = true, download = true }) =>
    <div className="wd-doc-list">
        {items.map((d, i) => <div key={d.title} className="item">
            <div className="t"><strong>{d.title}</strong><span>{d.meta}</span></div>
            {showBadge && d.badge && <span className="badge-grey">{d.badge}</span>}
            {(likes || questionCount) && <span className="engagement">
                {likes && <span className={`count${i === 0 ? ' hot' : ''}`}><FiThumbsUp />{likeCount && d.likes !== undefined && ` ${d.likes}`}</span>}
                {questionCount && d.questions !== undefined && <span className="count"><FiMessageCircle /> {d.questions}</span>}
            </span>}
            {download && <FiDownload className="dl" />}
        </div>)}
    </div>;
